import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal, Select, Spin, message } from 'antd';
import {
  createEacStaff,
  getEacStaffList,
  reactivateEacStaff,
  removeEacStaff,
  type EacProjectItem,
  type EacStaffItem,
} from '../../../api';

interface TeamManagementDrawerProps {
  open: boolean;
  project?: EacProjectItem;
  onClose: () => void;
  onSaved?: () => void | Promise<void>;
}

interface MockEmployeeOption {
  key: string;
  name: string;
  job: string;
  hourlyRate: number;
  actualCost: number;
}

interface TeamMemberRow {
  uid: string;
  id?: number;
  projectCode: string;
  name: string;
  job: string;
  hourlyRate: number;
  status: 'Active' | 'Inactive';
  joinedDate: string;
  actualCost: number;
  estimatedCost: number;
  source: 'server' | 'new';
}

const DRAWER_TOP_OFFSET = 168;
const DRAWER_RIGHT_OFFSET = 8;
const DRAWER_BOTTOM_OFFSET = 12;
const DRAWER_WIDTH = 680;
const DRAWER_PROJECT_CODE = 'PROJ-2026-089';
const NEW_MEMBER_JOINED_DATE = '2026-10-01';
const EMPTY_STAFF_LIST: EacStaffItem[] = [];

/**
 * 团队管理弹窗里可选员工：按需求使用前端 mock。
 * 说明：可按后续演示需要继续扩展此列表。
 */
const MOCK_EMPLOYEES: MockEmployeeOption[] = [
  {
    key: 'leo-wilson',
    name: 'Leo Wilson',
    job: 'Senior Engineer',
    hourlyRate: 2000,
    actualCost: 0,
  },
];

const formatDate = (value: string): string => {
  if (!value) return '--';
  return value.slice(0, 10);
};

const formatRate = (value: number): string => {
  if (!Number.isFinite(value)) return '--';
  if (Number.isInteger(value)) return `${value}`;
  return value
    .toFixed(2)
    .replace(/\.0+$/, '')
    .replace(/(\.\d*[1-9])0+$/, '$1');
};

/** 将后端 staff 数据映射为抽屉内部渲染结构。 */
const mapStaffItemToRow = (item: EacStaffItem): TeamMemberRow => {
  return {
    uid: `server-${item.id}`,
    id: item.id,
    projectCode: item.projectCode,
    name: item.name,
    job: item.job,
    hourlyRate: item.hourlyRate,
    status: item.status === 'Active' ? 'Active' : 'Inactive',
    joinedDate: formatDate(item.createdAt),
    actualCost: item.actualCost,
    estimatedCost: item.estimatedCost,
    source: 'server',
  };
};

const TeamManagementDrawer: React.FC<TeamManagementDrawerProps> = ({
  open,
  project,
  onClose,
  onSaved,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [members, setMembers] = useState<TeamMemberRow[]>([]);
  const [backendLocked, setBackendLocked] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [selectedEmployeeKey, setSelectedEmployeeKey] = useState<
    string | undefined
  >(undefined);
  const [saving, setSaving] = useState(false);
  const [actionPendingUid, setActionPendingUid] = useState<string | null>(null);

  // 仅在抽屉打开且项目存在时拉取人员列表。
  const {
    data: staffList = EMPTY_STAFF_LIST,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['eac-staff-list', project?.code],
    queryFn: () => getEacStaffList(project!.code),
    enabled: open && Boolean(project?.code),
  });

  useEffect(() => {
    if (!(error instanceof Error)) return;
    messageApi.error(error.message);
  }, [error, messageApi]);

  useEffect(() => {
    if (!open) return;
    const mappedRows = staffList.map(mapStaffItemToRow);
    setMembers(mappedRows);
    // 按需求：当后端已存在两条及以上数据时，本弹窗除关闭外都禁止操作。
    setBackendLocked(staffList.length >= 2);
  }, [open, staffList]);

  useEffect(() => {
    if (open) return;
    setAddMemberOpen(false);
    setSelectedEmployeeKey(undefined);
    setSaving(false);
    setActionPendingUid(null);
  }, [open]);

  const existingNames = useMemo(() => {
    return new Set(members.map(member => member.name.toLowerCase()));
  }, [members]);

  const availableEmployees = useMemo(() => {
    return MOCK_EMPLOYEES.filter(
      employee => !existingNames.has(employee.name.toLowerCase())
    );
  }, [existingNames]);

  const selectedEmployee = useMemo(() => {
    return availableEmployees.find(
      employee => employee.key === selectedEmployeeKey
    );
  }, [availableEmployees, selectedEmployeeKey]);

  useEffect(() => {
    if (!selectedEmployeeKey) return;
    const stillAvailable = availableEmployees.some(
      employee => employee.key === selectedEmployeeKey
    );
    if (!stillAvailable) {
      setSelectedEmployeeKey(undefined);
    }
  }, [availableEmployees, selectedEmployeeKey]);

  const serverMembers = useMemo(() => {
    return members.filter(member => member.source === 'server');
  }, [members]);

  const newMembers = useMemo(() => {
    return members.filter(member => member.source === 'new');
  }, [members]);

  const hasActiveMember = useMemo(() => {
    return members.some(member => member.status === 'Active');
  }, [members]);

  // 按文档逻辑：原始成员需要被 remove（即都为 Inactive）后才允许 Save。
  const allServerMembersInactive = useMemo(() => {
    return serverMembers.every(member => member.status === 'Inactive');
  }, [serverMembers]);

  const saveEnabled = useMemo(() => {
    if (backendLocked || saving || actionPendingUid !== null) return false;
    if (members.length < 2) return false;
    if (newMembers.length === 0) return false;
    if (!hasActiveMember) return false;
    if (!allServerMembersInactive) return false;
    return true;
  }, [
    actionPendingUid,
    allServerMembersInactive,
    backendLocked,
    hasActiveMember,
    members.length,
    newMembers.length,
    saving,
  ]);

  const handleOpenAddMember = () => {
    if (backendLocked) return;
    if (saving || actionPendingUid !== null) return;
    setSelectedEmployeeKey(undefined);
    setAddMemberOpen(true);
  };

  const handleAddMember = () => {
    if (!selectedEmployee || !project?.code) return;

    // 新增成员时，预计成本按当前项目预计工时 * 人员时薪动态计算。
    const estimatedCost = project.estimatedHours * selectedEmployee.hourlyRate;

    const appendedMember: TeamMemberRow = {
      uid: `new-${selectedEmployee.key}`,
      projectCode: project.code,
      name: selectedEmployee.name,
      job: selectedEmployee.job,
      hourlyRate: selectedEmployee.hourlyRate,
      status: 'Active',
      joinedDate: NEW_MEMBER_JOINED_DATE,
      actualCost: selectedEmployee.actualCost,
      estimatedCost,
      source: 'new',
    };

    setMembers(prev => [...prev, appendedMember]);
    setAddMemberOpen(false);
    setSelectedEmployeeKey(undefined);
  };

  const handleToggleStaffStatus = async (member: TeamMemberRow) => {
    if (backendLocked || saving) return;
    if (member.source !== 'server' || !member.id) return;

    setActionPendingUid(member.uid);
    try {
      if (member.status === 'Active') {
        await removeEacStaff(member.id);
        setMembers(prev =>
          prev.map(item =>
            item.uid === member.uid ? { ...item, status: 'Inactive' } : item
          )
        );
      } else {
        await reactivateEacStaff(member.id);
        setMembers(prev =>
          prev.map(item =>
            item.uid === member.uid ? { ...item, status: 'Active' } : item
          )
        );
      }
    } catch (err) {
      messageApi.error((err as Error).message || '更新成员状态失败');
    } finally {
      setActionPendingUid(null);
    }
  };

  const handleSave = async () => {
    if (!saveEnabled || !project?.code) return;

    setSaving(true);
    try {
      await Promise.all(
        newMembers.map(member =>
          createEacStaff({
            projectCode: member.projectCode,
            job: member.job,
            name: member.name,
            hourlyRate: member.hourlyRate,
            actualCost: member.actualCost,
            estimatedCost: member.estimatedCost,
          })
        )
      );

      messageApi.success('Team member saved successfully');
      await refetch();
      if (onSaved) {
        await onSaved();
      }
      onClose();
    } catch (err) {
      messageApi.error((err as Error).message || '保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const canOperate = !backendLocked && !saving && actionPendingUid === null;

  return (
    <>
      {contextHolder}
      {open ? (
        <div className="pointer-events-none fixed inset-0 z-[1000]">
          <div
            className="pointer-events-auto absolute flex flex-col overflow-hidden rounded-[10px] border border-[#d9d9d9] bg-white shadow-[0_6px_18px_rgba(15,23,42,0.18)]"
            style={{
              top: DRAWER_TOP_OFFSET,
              right: DRAWER_RIGHT_OFFSET,
              bottom: DRAWER_BOTTOM_OFFSET,
              width: DRAWER_WIDTH,
            }}
          >
            <div className="border-b border-[#d9d9d9] px-5 py-[11px]">
              <span className="text-[20px] font-semibold leading-none text-[#2f333a]">
                Project Team Management
              </span>
            </div>
            <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
              {isLoading ? (
                <div className="flex flex-1 items-center justify-center">
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div className="pr-3">
                      <div className="mb-[6px] text-[12px] leading-none text-[#8a919f]">
                        Project
                      </div>
                      <div className="text-[14px] leading-[1.5] text-[#2f333a]">
                        {project?.name || '--'} (ID: {project?.code})
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleOpenAddMember}
                      disabled={!canOperate}
                      className="h-[30px] shrink-0 cursor-pointer rounded-md border-none bg-[#245ff0] px-4 text-[12px] font-medium text-white disabled:cursor-not-allowed disabled:bg-[#aec4f7]"
                    >
                      + Add Member
                    </button>
                  </div>

                  <div className="mb-3 text-[13px] font-semibold leading-none text-[#2f333a]">
                    Team Members
                  </div>

                  <div className="overflow-hidden border-t border-[#eceff3]">
                    <table className="w-full border-collapse text-[12px] text-[#3f4855]">
                      <thead>
                        <tr className="border-b border-[#eceff3] text-[#8b93a1]">
                          <th className="w-[110px] px-[10px] py-[9px] text-left font-normal">
                            Name
                          </th>
                          <th className="w-[154px] px-[10px] py-[9px] text-left font-normal">
                            Position
                          </th>
                          <th className="w-[82px] px-[10px] py-[9px] text-left font-normal">
                            Status
                          </th>
                          <th className="w-[94px] px-[10px] py-[9px] text-left font-normal">
                            HourRate(￥)
                          </th>
                          <th className="w-[110px] px-[10px] py-[9px] text-left font-normal">
                            Joined date
                          </th>
                          <th className="w-[90px] px-[10px] py-[9px] text-left font-normal">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map(member => {
                          const actionLoading = actionPendingUid === member.uid;
                          const canClickAction =
                            canOperate && member.source === 'server';
                          const actionText =
                            member.status === 'Active'
                              ? 'Remove'
                              : 'Reactivate';

                          return (
                            <tr key={member.uid}>
                              <td className="px-[10px] py-[12px] align-middle">
                                {member.name}
                              </td>
                              <td className="px-[10px] py-[12px] align-middle">
                                {member.job}
                              </td>
                              <td className="px-[10px] py-[12px] align-middle">
                                <span
                                  className={`inline-flex min-w-[46px] justify-center rounded-full px-[8px] py-[1px] text-[10px] leading-[18px] ${
                                    member.status === 'Active'
                                      ? 'bg-[#c9ecd6] text-[#08a33d]'
                                      : 'bg-[#f8dfe3] text-[#ef2438]'
                                  }`}
                                >
                                  {member.status}
                                </span>
                              </td>
                              <td className="px-[10px] py-[12px] align-middle">
                                {formatRate(member.hourlyRate)}
                              </td>
                              <td className="px-[10px] py-[12px] align-middle">
                                {member.joinedDate}
                              </td>
                              <td className="px-[10px] py-[12px] align-middle">
                                <button
                                  type="button"
                                  disabled={!canClickAction || actionLoading}
                                  onClick={() =>
                                    void handleToggleStaffStatus(member)
                                  }
                                  className={`min-h-[24px] min-w-[74px] cursor-pointer bg-transparent px-[6px] text-left text-[12px] leading-none shadow-none outline-none ring-0 transition-colors disabled:cursor-not-allowed ${
                                    member.source === 'new'
                                      ? 'text-[#b6b6b6]'
                                      : member.status === 'Active'
                                        ? 'text-[#ff1c2d] disabled:text-[#b6b6b6]'
                                        : 'rounded-sm bg-[#f3f4f6] text-[#129bfe] disabled:text-[#b6b6b6]'
                                  }`}
                                  style={{
                                    border: 'none',
                                    boxShadow: 'none',
                                  }}
                                >
                                  {actionLoading ? '...' : actionText}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-auto flex justify-end gap-3 pt-8">
                    <Button
                      onClick={onClose}
                      className="!h-8 !min-w-[80px] !rounded-sm !border-[#d1d5db] !bg-white !px-4 !text-[12px] !text-[#535c68]"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => void handleSave()}
                      loading={saving}
                      disabled={!saveEnabled}
                      className="!h-8 !min-w-[80px] !rounded-sm !border-none !px-4 !text-[12px]"
                    >
                      Save
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <Modal
        title={
          <span className="text-[16px] font-semibold text-[#2f333a]">
            Add Project Member
          </span>
        }
        open={addMemberOpen}
        onCancel={() => setAddMemberOpen(false)}
        footer={null}
        width={620}
        styles={{
          content: { borderRadius: 12, padding: 0 },
          header: {
            borderBottom: '1px solid #e5e7eb',
            marginBottom: 0,
            padding: '14px 18px',
          },
          body: { padding: '20px 24px 24px' },
        }}
      >
        <div>
          <div className="mb-2 text-[12px] font-semibold text-[#2f333a]">
            Select Employee
          </div>
          <Select
            value={selectedEmployeeKey}
            onChange={value => setSelectedEmployeeKey(value)}
            placeholder="-- Choose an employee --"
            className="!w-full"
            style={{ height: 40 }}
            options={availableEmployees.map(employee => ({
              value: employee.key,
              label: employee.name,
            }))}
          />

          {selectedEmployee ? (
            <>
              <div className="mt-6 text-[12px] font-semibold text-[#2f333a]">
                Position
              </div>
              <div className="mt-1 text-[18px] leading-[1.5] text-[#2f333a]">
                {selectedEmployee.job}
              </div>

              <div className="mt-6 text-[12px] font-semibold text-[#2f333a]">
                Hourly Rate (￥)
              </div>
              <div className="mt-1 text-[18px] leading-[1.5] text-[#2f333a]">
                {formatRate(selectedEmployee.hourlyRate)} Yuan/h
              </div>
            </>
          ) : null}

          <div className="mt-10 flex justify-end gap-4">
            <Button
              onClick={() => setAddMemberOpen(false)}
              className="!h-9 !min-w-[116px] !rounded-sm !border-[#d1d5db] !bg-white !text-[12px] !text-[#535c68]"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleAddMember}
              disabled={!selectedEmployee}
              className="!h-9 !min-w-[128px] !rounded-sm !border-none !text-[12px]"
            >
              Add to Project
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TeamManagementDrawer;
